import React, { useState, useEffect } from "react";
import { Tv } from "lucide-react";
import { getPopularTvShows, getImageUrl } from "../../services/tmdbApi";
import { FilterBar } from "../../components/FilterBar";
import "./style.css";

const ITEMS_PER_PAGE = 8;
const MAX_RESULTS = 100;

const TVShowsPage = () => {
  const [tvShows, setTvShows] = useState([]);
  const [filteredTvShows, setFilteredTvShows] = useState([]);
  const [viewMode, setViewMode] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchAllTvShows = async () => {
      let allTvShows = [];
      for (let page = 1; page <= 5; page++) {
        const pageTvShows = await getPopularTvShows(page);
        allTvShows = allTvShows.concat(pageTvShows);
        if (allTvShows.length >= MAX_RESULTS) break;
      }
      allTvShows = allTvShows.slice(0, MAX_RESULTS);
      setTvShows(allTvShows);
      setFilteredTvShows(allTvShows);
      setLoading(false);
    };
    fetchAllTvShows();
  }, []);

  // Filter logic
  const handleFilterChange = (filters) => {
    let filtered = tvShows;

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(
        (show) =>
          show.name.toLowerCase().includes(filters.search.toLowerCase()) ||
          show.overview.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    // Rating filter
    if (filters.rating && filters.rating !== "all") {
      const [min, max] = filters.rating.split("-").map(Number);
      filtered = filtered.filter(
        (show) => show.vote_average >= min && show.vote_average <= max
      );
    }

    // Sorting
    switch (filters.sortBy) {
      case "rating-high":
        filtered = [...filtered].sort(
          (a, b) => b.vote_average - a.vote_average
        );
        break;
      case "rating-low":
        filtered = [...filtered].sort(
          (a, b) => a.vote_average - b.vote_average
        );
        break;
      case "title":
        filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
        break;
      case "oldest":
        filtered = [...filtered].sort((a, b) =>
          new Date(a.first_air_date).getTime() - new Date(b.first_air_date).getTime()
        );
        break;
      default: // newest
        filtered = [...filtered].sort((a, b) =>
          new Date(b.first_air_date).getTime() - new Date(a.first_air_date).getTime()
        );
    }

    setFilteredTvShows(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredTvShows.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIdx = startIdx + ITEMS_PER_PAGE;
  const paginatedTvShows = filteredTvShows.slice(startIdx, endIdx);

  const handlePrev = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNext = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  if (loading) {
    return (
      <div className="tvshows-loading-container">
        <div className="tvshows-loading-spinner" />
        <span>Loading TV shows...</span>
      </div>
    );
  }

  return (
    <div className="tvshows-page-container">
      <div className="tvshows-page-content">
        {/* Header */}
        <div className="tvshows-header">
          <div className="tvshows-header-icon">
            <Tv size={28} />
          </div>
          <div>
            <h1 className="tvshows-title">TV Show Reviews</h1>
            <p className="tvshows-subtitle">
              Popular TV shows from The Movie Database
            </p>
          </div>
        </div>
        <div className="tvshows-header-row">
          <p className="tvshows-count">
            Showing {filteredTvShows.length} of {tvShows.length} TV shows
          </p>
          <div className="tvshows-view-toggle">
            <button
              className={`tvshows-toggle-btn${
                viewMode === "grid" ? " active" : ""
              }`}
              onClick={() => setViewMode("grid")}
            >
              Grid
            </button>
            <button
              className={`tvshows-toggle-btn${
                viewMode === "list" ? " active" : ""
              }`}
              onClick={() => setViewMode("list")}
            >
              List
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="tvshows-filter-bar">
          <FilterBar category="tv" onFilterChange={handleFilterChange} />
        </div>

        {/* TV Shows Grid/List */}
        <div
          className={
            viewMode === "grid"
              ? "tvshows-grid"
              : "tvshows-list"
          }
        >
          {paginatedTvShows.map((show) => (
            <div
              key={show.id}
              className={
                viewMode === "list"
                  ? "tvshows-list-card"
                  : "tvshows-grid-card"
              }
            >
              <img
                src={getImageUrl(show.poster_path)}
                alt={show.name}
                className="tvshows-card-poster"
              />
              <div className="tvshows-card-content">
                <h3 className="tvshows-card-title">{show.name}</h3>
                <p className="tvshows-card-date">
                  {show.first_air_date
                    ? new Date(show.first_air_date).getFullYear()
                    : "TBA"}
                </p>
                <p className="tvshows-card-excerpt">
                  {show.overview && show.overview.length > 120
                    ? show.overview.substring(0, 120) + "..."
                    : show.overview}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="tvshows-pagination">
            <button
              className="tvshows-pagination-btn"
              onClick={handlePrev}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span className="tvshows-pagination-info">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="tvshows-pagination-btn"
              onClick={handleNext}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        )}

        {/* No Results */}
        {paginatedTvShows.length === 0 && !loading && (
          <div className="tvshows-no-results">
            <Tv size={48} />
            <h3>No TV shows found</h3>
            <p>Try adjusting your filters to find what you're looking for.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TVShowsPage;